import { gql } from "@apollo/client";

import { NEARBY_ALERTS_FIELDS } from "./appConfig.queries";

/*
 * `updateBlockedCountryPrefixes` used to live here, behind a chip editor on
 * this screen. It was removed from the schema along with the rest of the
 * raw-prefix mechanism — country blocking is now managed only from the Country
 * Blocking screen, keyed by ISO country rather than dialling prefix, and the
 * 14 prefixes this used to write were migrated into that list.
 *
 * Calling it now fails with `Cannot query field "updateBlockedCountryPrefixes"`.
 * It also never worked properly: it used `updateMany`, which matched nothing
 * when the row did not exist, so it reported success while changing nothing.
 */

/**
 * Partial update — send only the fields the form changed.
 *
 * The server merges the patch onto the stored row and validates the *result*,
 * so a cross-field rule (`proximityRadiusMeters` ≥ 2 × `businessRadiusMeters`)
 * still fires when only one side of the pair was edited. That is why the form
 * must not send a full config on every save: doing so would be harmless here
 * but would silently overwrite a field a newer client added that this dashboard
 * does not know about.
 *
 * Rejects rather than clamps, and names the offending field in
 * `extensions.field` so the error can be attached to the input rather than
 * shown as a banner. See `fieldErrorOf`.
 */
export const UPDATE_NEARBY_ALERTS_CONFIG = gql`
  mutation UpdateNearbyAlertsConfig($input: NearbyAlertsConfigInput!) {
    updateNearbyAlertsConfig(input: $input) {
      ...NearbyAlertsFields
    }
  }
  ${NEARBY_ALERTS_FIELDS}
`;

/**
 * The fleet-wide kill switch — its own row, and its own mutation.
 *
 * Separate from the tuning on purpose: it is the thing somebody reaches for
 * during an incident, and it keeps working when the tuning row is unparseable.
 * `false` stops tracking for every member at their next cold start whatever
 * they chose in their own settings — it is not a default.
 */
export const UPDATE_NEARBY_ALERTS_ENABLED = gql`
  mutation UpdateNearbyAlertsEnabled($enabled: Boolean!) {
    updateNearbyAlertsEnabled(enabled: $enabled) {
      ...NearbyAlertsFields
    }
  }
  ${NEARBY_ALERTS_FIELDS}
`;

/**
 * Sets — or clears — the lowest app version still allowed.
 *
 * Returns the stored value, so a save needs no read-back: it is the only proof
 * of which of the two outcomes happened, since a refused write leaves the row
 * exactly as it was. `null` comes back when the row was deleted.
 *
 * `$version` is nullable because clearing is `version: null`, which **deletes**
 * the row rather than storing an empty string. Omitting the argument entirely
 * clears it too, but this form always sends the field explicitly — an operation
 * whose meaning depends on a missing variable is one serialiser bug away from
 * ending a rollout nobody meant to end.
 *
 * A malformed value is refused with `INVALID_APP_VERSION` and
 * `extensions.field: "version"`, so the message can be attached to the input —
 * see `fieldErrorOf`. It is refused rather than stored because the client
 * ignores anything it cannot parse: a stored typo would look set here and
 * enforce nothing.
 */
export const SET_MIN_APP_VERSION = gql`
  mutation SetMinAppVersion($version: String) {
    setMinAppVersion(version: $version)
  }
`;

/**
 * Sets the score a request needs to get a sign-in code. Returns the settings as
 * the server now holds them, so a save needs no read-back.
 */
export const SET_RECAPTCHA_SCORE_THRESHOLD = gql`
  mutation SetRecaptchaScoreThreshold($threshold: Float!) {
    setRecaptchaScoreThreshold(threshold: $threshold) {
      scoreThreshold
      setFromDashboard
      serverDefault
      min
      max
      updatedAt
      warning
    }
  }
`;

export const SET_CRM_EMAIL_SEND_PER_RUN = gql`
  mutation SetCrmEmailSendPerRun($perRun: Int!) {
    setCrmEmailSendPerRun(perRun: $perRun) {
      perRun
      setFromDashboard
      serverDefault
      min
      max
      sendCron
      sendingEnabled
      updatedAt
      warning
    }
  }
`;
